import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useGradient } from './GradientContext';

interface GradientBackgroundProps {
    className?: string;
}

const GradientBackground: React.FC<GradientBackgroundProps> = ({ className = '' }) => {
    const { gradientColors, gradientOpacity } = useGradient();
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Canvas animation effect for dynamic gradient
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        // Convert Tailwind color classes to actual colors
        const getColorFromClass = (colorClass: string) => {
            // Parse accent color or other tailwind colors
            if (colorClass.startsWith('accent')) {
                return '#0070F3'; // The accent color defined in tailwind config
            }

            // For other colors like emerald-500/30, etc.
            // This is a simplified version - in a real app you'd need a more sophisticated conversion
            if (colorClass.includes('emerald')) return '#10B981';
            if (colorClass.includes('teal')) return '#14B8A6';
            if (colorClass.includes('amber')) return '#F59E0B';
            if (colorClass.includes('yellow')) return '#EAB308';
            if (colorClass.includes('rose')) return '#F43F5E';
            if (colorClass.includes('red')) return '#EF4444';

            // Default fallback
            return '#0070F3';
        };

        // Parse opacity
        const getOpacity = (colorClass: string) => {
            if (colorClass.includes('/')) {
                const opacity = colorClass.split('/')[1];
                return parseInt(opacity) / 100;
            }
            return 1;
        };

        // Get colors with correct opacity
        const fromColor = getColorFromClass(gradientColors.from);
        const toColor = getColorFromClass(gradientColors.to);
        const fromOpacity = getOpacity(gradientColors.from) * gradientOpacity;
        const toOpacity = getOpacity(gradientColors.to) * gradientOpacity;

        // Gradient animation variables
        let time = 0;

        const animate = () => {
            time += 0.003;

            // Clear canvas
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Create dynamic gradient
            const x1 = canvas.width * (0.5 + 0.3 * Math.sin(time));
            const y1 = canvas.height * (0.5 + 0.2 * Math.cos(time));
            const x2 = canvas.width * (0.5 - 0.3 * Math.sin(time * 0.7));
            const y2 = canvas.height * (0.5 - 0.2 * Math.cos(time * 0.7));

            const gradient = ctx.createRadialGradient(x1, y1, 0, x2, y2, canvas.width * 0.7);
            gradient.addColorStop(0, `${fromColor}${fromOpacity.toString(16).padStart(2, '0')}`);
            gradient.addColorStop(1, `${toColor}${toOpacity.toString(16).padStart(2, '0')}`);

            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            requestAnimationFrame(animate);
        };

        const animationId = requestAnimationFrame(animate);

        const handleResize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationId);
        };
    }, [gradientColors, gradientOpacity]);

    return (
        <div className={`fixed inset-0 overflow-hidden pointer-events-none ${className}`}>
            <canvas
                ref={canvasRef}
                className="w-full h-full opacity-30"
            />

            {/* Additional subtle animated elements */}
            <motion.div
                className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-gradient-to-tr from-accent/10 to-accent/5 blur-3xl"
                animate={{
                    scale: [1, 1.05, 1],
                    opacity: [0.2, 0.3, 0.2]
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut"
                }}
            />

            <motion.div
                className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-gradient-to-br from-accent/10 to-accent/5 blur-3xl"
                animate={{
                    scale: [1, 1.05, 1],
                    opacity: [0.2, 0.3, 0.2]
                }}
                transition={{
                    duration: 10,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut"
                }}
            />
        </div>
    );
};

export default GradientBackground; 